// src/screens/admin/CotizadorImagen.jsx
// "Itinerario para el cliente" de una cotización del Cotizador BV: una imagen con el
// recorrido de la opción de día que tú ya escogiste (llegada a Boa Vista, noches de
// hostal, ómnibus, vuelo). No compara días. Muestra solo el viaje; el precio en US$ es
// opcional y nunca salen costos ni ganancia (eso solo lo ves tú aquí, junto a cada opción).
import React, { useState, useEffect, useMemo } from 'react';
import { toast } from '../../components/UI';
import { itinerarioOpcion, mejorOpcion, fmtFecha } from '../../services/cotizadorCalc';
import { renderItinerario } from '../../services/itinerarioRender';
import { canvasToBlob, canvasToPdfBlob, FORMATOS } from '../../services/orcamentoRender';
import ExportarArte, { CSS_ARTE, TIPOS, TIPO_POR_FORMATO, entregarArchivo, Interruptor } from './ExportarArte';
import { Campo, inputStyle, labelStyle, btnChico, ESTADOS, diaSemana } from './CotizadorUI';

const noches = (n) => `${n} ${n === 1 ? 'noche' : 'noches'}`;

export default function CotizadorImagen({ cot, ajustes, horarios, resultados, onBack }) {
  const itinerarios = useMemo(() => cot.opciones.map((op) => (
    itinerarioOpcion({ cotizacion: cot, opcion: op, ajustes, horarios })
  )), [cot, ajustes, horarios]);

  // Por defecto: la mejor opción (mayor ganancia) si tiene recorrido; si no, la primera que lo tenga.
  const [elegida, setElegida] = useState(() => {
    const mejor = mejorOpcion(resultados);
    const i = mejor >= 0 && itinerarios[mejor] ? mejor : itinerarios.findIndex(Boolean);
    return i >= 0 ? cot.opciones[i].id : null;
  });
  const [cliente, setCliente] = useState('');
  const [destino, setDestino] = useState(() => cot.opciones.find((o) => (o.ciudad || '').trim())?.ciudad?.trim() || '');
  const [idioma, setIdioma] = useState('pt');
  const [llegadaBVHora, setLlegadaBVHora] = useState('');
  const [llegaPorOpcion, setLlegaPorOpcion] = useState({}); // hora de llegada del vuelo escrita aquí, por opción
  const [mostrarPrecio, setMostrarPrecio] = useState(false);
  const [mostrarReales, setMostrarReales] = useState(false);
  const [obs, setObs] = useState('');
  const [formato, setFormato] = useState('story');
  const [tipo, setTipo] = useState('png');
  const [vista, setVista] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [desbordo, setDesbordo] = useState(false);
  const [exportando, setExportando] = useState('');

  const usd = Number(cot.usd);
  const tasa = Number(cot.tasa);
  const idx = cot.opciones.findIndex((o) => o.id === elegida);
  const base = idx >= 0 ? itinerarios[idx] : null;
  const llegaVuelo = idx >= 0 ? (llegaPorOpcion[elegida] ?? cot.opciones[idx].hora_llegada_vuelo ?? '') : '';
  const itinerario = useMemo(() => (base ? { ...base, llegadaBVHora: llegadaBVHora || null, vuelo: { ...base.vuelo, llega: llegaVuelo || null } } : null), [base, llegadaBVHora, llegaVuelo]);

  const datos = useMemo(() => ({
    idioma, destinoCiudad: destino.trim(), cliente: cliente.trim(), itinerario,
    mostrarPrecio, mostrarReales: mostrarPrecio && mostrarReales, precioUsd: usd, tasa, reales: usd * tasa,
    notas: obs.split('\n').map((x) => x.trim()).filter(Boolean),
  }), [idioma, destino, cliente, itinerario, mostrarPrecio, mostrarReales, usd, tasa, obs]);

  const listo = !!itinerario && (!mostrarPrecio || usd > 0);

  useEffect(() => {
    if (!listo) { setVista(null); return undefined; }
    let vivo = true;
    setCargando(true);
    const t = setTimeout(async () => {
      try {
        const c = await renderItinerario(datos, { formato, scale: formato === 'a4' ? 0.7 : 0.6, logoUrl: `${process.env.PUBLIC_URL || ''}/logo-principal.png` });
        if (vivo) { setVista(c.toDataURL('image/jpeg', 0.85)); setDesbordo(!!c.desbordo); }
      } catch { if (vivo) setVista(null); }
      if (vivo) setCargando(false);
    }, 450);
    return () => { vivo = false; clearTimeout(t); };
  }, [datos, listo, formato]);

  const elegirFormato = (id) => { setFormato(id); setTipo(TIPO_POR_FORMATO[id]); };

  const exportar = async (modo) => {
    if (!listo) return;
    setExportando(modo);
    try {
      const fm = FORMATOS[formato];
      const t = TIPOS.find((x) => x.id === tipo);
      const canvas = await renderItinerario(datos, { formato, scale: fm.escala, logoUrl: `${process.env.PUBLIC_URL || ''}/logo-principal.png` });
      const blob = tipo === 'pdf' ? await canvasToPdfBlob(canvas)
        : tipo === 'png' ? await canvasToBlob(canvas, 1, 'image/png') : await canvasToBlob(canvas, 0.93);
      const nombre = `${['itinerario', cliente.trim() || destino.trim(), formato].filter(Boolean).join('-').replace(/\s+/g, '-')}.${t.ext}`;
      const r = await entregarArchivo({ blob, nombre, mime: t.mime, modo });
      if (r === 'cancelado') { setExportando(''); return; }
      if (modo === 'compartir' && r === 'descargado') toast('Este dispositivo no permite compartir: se descargó el archivo.');
      toast(`${t.label} listo.`);
    } catch { toast('No se pudo generar la imagen.', 'error'); }
    setExportando('');
  };

  return (
    <div>
      <style>{CSS_ARTE}</style>
      <button onClick={onBack} style={{ ...btnChico, marginBottom: 12 }}>← Volver a la cotización</button>
      <h3 style={{ fontSize: 17, fontWeight: 800, color: 'var(--white)', margin: '0 0 4px' }}>Itinerario para el cliente</h3>
      <p style={{ fontSize: 12, color: 'var(--muted)', margin: '0 0 14px', lineHeight: 1.45 }}>
        Elige la opción que ya escogiste: el cliente recibe su recorrido completo, día por día.
      </p>

      <div style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 18, padding: 16, marginBottom: 14 }}>
        <label style={labelStyle}>Opción escogida</label>
        <div style={{ display: 'grid', gap: 8 }} role="radiogroup" aria-label="Opción escogida">
          {cot.opciones.map((op, i) => {
            const it = itinerarios[i]; const r = resultados[i]; const est = r?.estado ? ESTADOS[r.estado] : null;
            const on = elegida === op.id && !!it;
            return (
              <button key={op.id} type="button" role="radio" aria-checked={on} disabled={!it} onClick={() => setElegida(op.id)} style={{
                display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left', minHeight: 60, padding: '10px 12px', borderRadius: 14, cursor: it ? 'pointer' : 'not-allowed',
                background: on ? 'rgba(255,107,0,0.10)' : 'var(--navy-card)', border: `1.5px solid ${on ? 'var(--orange)' : 'var(--navy-border)'}`, color: 'var(--white)', opacity: it ? 1 : 0.55,
              }}>
                <span aria-hidden="true" style={{ width: 24, height: 24, borderRadius: 12, border: `2px solid ${on ? 'var(--orange)' : 'var(--navy-border)'}`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {on && <span style={{ width: 12, height: 12, borderRadius: 6, background: 'var(--orange)' }} />}
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 14, fontWeight: 700 }}>
                    {fmtFecha(op.dia_vuelo)} <span style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 600 }}>{diaSemana(op.dia_vuelo)}</span>
                    {op.ciudad ? <span style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 500 }}> · {op.ciudad}</span> : null}
                  </span>
                  <span style={{ display: 'block', fontSize: 12, color: 'var(--muted)' }}>
                    {it
                      ? `${noches(it.noches)} · ómnibus sale ${fmtFecha(it.bus.saleIso)} ${it.bus.sale} · vuelo ${it.vuelo.sale}`
                      : (op.hora_vuelo ? 'Ningún ómnibus llega a tiempo' : 'Falta la hora del vuelo')}
                  </span>
                </span>
                {est && <span style={{ fontSize: 11, fontWeight: 700, color: est.color, flexShrink: 0 }} title="Solo lo ves tú">{est.corto}</span>}
              </button>
            );
          })}
        </div>
        <p style={{ fontSize: 11, color: 'var(--muted)', margin: '8px 0 0' }}>La etiqueta de color (Bien, Poca, Pierdes) solo la ves tú: no sale en la imagen.</p>
      </div>

      <div style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 18, padding: 16, marginBottom: 14 }}>
        <Campo label="Nombre del cliente en la imagen (opcional)" style={{ marginBottom: 10 }}>
          <input value={cliente} onChange={(e) => setCliente(e.target.value)} placeholder="Marta" style={{ ...inputStyle, minHeight: 44 }} />
        </Campo>
        <Campo label="Destino (título)" style={{ marginBottom: 12 }}>
          <input value={destino} onChange={(e) => setDestino(e.target.value)} placeholder="Porto Alegre" style={{ ...inputStyle, minHeight: 44 }} />
        </Campo>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 4 }}>
          <Campo label="Llegada a Boa Vista (hora, opcional)">
            <input type="time" value={llegadaBVHora} onChange={(e) => setLlegadaBVHora(e.target.value)} style={{ ...inputStyle, minHeight: 44 }} />
          </Campo>
          <Campo label="Llegada del vuelo (hora)">
            <input type="time" value={llegaVuelo} onChange={(e) => setLlegaPorOpcion((m) => ({ ...m, [elegida]: e.target.value }))} style={{ ...inputStyle, minHeight: 44, borderColor: base && !llegaVuelo ? 'var(--gold)' : undefined }} />
          </Campo>
        </div>
        <p style={{ fontSize: 11, color: base && !llegaVuelo ? 'var(--gold)' : 'var(--muted)', margin: '0 0 12px' }}>
          {base && !llegaVuelo ? 'Falta la hora de llegada del vuelo: ponla para que el cliente la vea. ' : ''}Si el vuelo llega después de medianoche, la imagen pone el día siguiente sola.
        </p>
        <Interruptor activo={mostrarPrecio} onChange={setMostrarPrecio}>Mostrar el precio del paquete (US$ {Number.isFinite(usd) ? usd : '—'})</Interruptor>
        {mostrarPrecio && (
          <Interruptor activo={mostrarReales} onChange={setMostrarReales}>También en reales (al cambio {String(tasa).replace('.', ',')})</Interruptor>
        )}
        <Campo label="Observaciones (una línea = un punto, opcional)">
          <textarea value={obs} maxLength={300} rows={3} onChange={(e) => setObs(e.target.value)} placeholder={idioma === 'es' ? 'Ej.: Llega a la terminal con 1 hora de anticipación.' : 'Ej.: Chegue à rodoviária com 1 hora de antecedência.'} style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.4 }} />
        </Campo>
        <p style={{ fontSize: 11, color: 'var(--muted)', margin: '6px 0 0' }}>Esto lo ve el cliente: escríbelo en el idioma de la imagen y no pongas CPF ni datos de pago.</p>
      </div>

      <div style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 18, padding: 16, marginBottom: 14 }}>
        <ExportarArte
          idioma={idioma} onIdioma={setIdioma} formato={formato} onFormato={elegirFormato} tipo={tipo} onTipo={setTipo}
          vista={vista} cargando={cargando} listo={listo} exportando={exportando} onExportar={exportar}
          textoVacio={itinerario ? 'Falta el precio al cliente (US$) en la cotización.' : 'Elige una opción para ver el itinerario.'}
          aviso={desbordo && (
            <p style={{ fontSize: 12, color: 'var(--gold)', margin: '10px 0 0', lineHeight: 1.4 }}>
              Tus observaciones no caben completas en {FORMATOS[formato].label} y se recortaron. Acórtalas o usa Story.
            </p>
          )}
        />
      </div>
    </div>
  );
}
